import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { Send, TriangleAlert } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import { formatCount } from '@/utils/number';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { usePublishPcmpUrgentBatch } from '../../hooks/usePcmpUrgentBatchActions';
import { extractBlockingProposalIds } from '../../utils/pcmpRequestErrors';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

const commentFormSchema = z.object({ comment: z.string().trim().max(PCMP_MKT_COMMENT_MAX) });

type CommentFormValues = z.infer<typeof commentFormSchema>;

interface PcmpUrgentPublishDialogProps {
  batchNo: number;
  itemCount: number;
  onClose: () => void;
}

/** Legacy urgent Publish: hand the whole staged batch to Accounts, all or nothing. */
export function PcmpUrgentPublishDialog({
  batchNo,
  itemCount,
  onClose,
}: PcmpUrgentPublishDialogProps) {
  const form = useForm<CommentFormValues>({
    resolver: zodResolver(commentFormSchema),
    defaultValues: { comment: '' },
  });
  const { mutate, isPending, error } = usePublishPcmpUrgentBatch(batchNo, onClose);
  const blocking = extractBlockingProposalIds(error);

  const onSubmit = form.handleSubmit((values) =>
    mutate(values.comment === '' ? undefined : values.comment),
  );

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Publish Urgent Batch #${batchNo}`}
        description={formatCount(itemCount, 'proposal')}
        icon={Send}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Publish"
        pendingLabel="Publishing..."
      >
        <div className="flex items-start gap-3 rounded-lg border border-warning-foreground/30 bg-warning px-4 py-3 text-sm text-warning-foreground">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Every proposal in the batch goes to Accounts for payment. A published batch cannot be
            rolled back here.
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Publishing stops if any proposal is on hold or Accounts is no longer pending on it. Roll
          those back or resolve them first.
        </p>
        {blocking.length > 0 ? (
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-muted-foreground">Holding the batch up</span>
            <ul className="flex flex-wrap gap-x-3 gap-y-1">
              {blocking.map((proposalId) => (
                <li key={proposalId}>
                  <Link
                    to="/pcmp/proposals/$proposalId"
                    params={{ proposalId }}
                    target="_blank"
                    className="font-mono text-primary hover:underline"
                  >
                    Proposal #{proposalId}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          placeholder="Optional note for the timeline"
          description="Recorded on each proposal's timeline."
        />
      </PcmpEditDialog>
    </Form>
  );
}
